import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { memo, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation } from "wouter";

import { orderFormSchema, type OrderFormData } from "../../model";

import { CargoParamsCard } from "./CargoParamsCard";
import { CustomerCard } from "./CustomerCard";
import { DirectionTransportationCard } from "./DirectionTransportationCard";
import { InformationCard } from "./InformationCard";
import { ScheduleNormsCard } from "./ScheduleNormsCard";
import { TradesCard } from "./TradesCard";
import { WagonDeliveryCard } from "./WagonDeliveryCard";

import {
  OrderDetailResponseDTO,
  OrderDetailResponseDTOStatus,
  UserProfileResponseDTO,
} from "@/common/api";
import { Button, ConfirmModal } from "@/common/components";
import { ROUTES } from "@/common/config";
import { formatDate, formatDatetimeLocal } from "@/common/lib";

const defaultValues: OrderFormData = {
  // Основная информация
  customerName: "",
  customerInn: "",
  createdDate: "",
  orderNumber: "",
  // Параметры груза
  cargoId: "",
  cargoWeight: 0,
  wagonType: "",
  wagonCode: "",
  cargoComments: "",
  wagonCarrying: 0,
  wagonVolume: 0,
  // График подачи вагонов
  deadlineAt: "",
  deadlineTo: "",
  decade: false,
  decadeFirst: 0,
  decadeSecond: 0,
  decadeThird: 0,
  decadeComments: "",
  // Графики и нормативы
  standartTimeLoading: 0,
  standartTimeUnloading: 0,
  penalty: 0,
  moratorium: false,
  // Торги
  tradeBegin: "",
  tradeEnd: "",
  collectionBegin: "",
  collectionEnd: "",
  retrading: false,
  retradingBegin: "",
  retradingEnd: "",
  resultsDate: "",
  // Направление перевозки
  loadingStation: "",
  loadingRoad: "",
  totalWagons: 0,
  unloadingDirection: [],
};

interface OrderFormProps {
  isPending: boolean;
  onSubmit: (data: OrderFormData) => void;
  onCancel: () => void;
  onDraft: (data: OrderFormData) => void;
  profileData: UserProfileResponseDTO;
  orderData?: OrderDetailResponseDTO;
}

export const OrderForm = memo<OrderFormProps>(
  ({ isPending, profileData, onSubmit, onDraft, orderData }) => {
    const [, setLocation] = useLocation();
    const [showDraftConfirm, setShowDraftConfirm] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

    const form = useForm<OrderFormData>({
      mode: "onChange",
      resolver: zodResolver(orderFormSchema),
      defaultValues: {
        ...defaultValues,
        customerName: profileData.companyInfo?.companyName ?? "",
        customerInn: profileData.companyInfo?.inn ?? "",
        orderNumber: `${format(new Date(), "MM.yy")}.${Math.floor(100000 + Math.random() * 900000)}`,
        createdDate: formatDate(new Date()) ?? "",
      },
    });

    const hasErrors = !form.formState.isValid;

    useEffect(() => {
      if (orderData)
        form.reset({
          // TODO: set createdDate
          orderNumber: orderData?.orderNumber ?? "",
          cargoId: orderData.cargoId?.cargoCode ?? "",
          cargoCode: orderData.cargoId?.cargoCode ?? "",
          cargoWeight: orderData.cargoWeight,
          wagonType: orderData.wagonType ?? "",
          wagonCode: orderData.modelId?.code ?? "",
          wagonCarrying: orderData.modelId?.liftingCapacity ?? 0,
          wagonVolume: orderData.modelId?.bodySize ?? 0,
          cargoComments: orderData.cargoComments ?? "",
          standartTimeLoading: orderData.standartTimeLoading || 0,
          standartTimeUnloading: orderData.standartTimeUnloading || 0,
          penalty: orderData.penalty,
          moratorium: orderData.moratorium,
          // Направление перевозки
          loadingStation: orderData.loadingStation?.code,
          codeStation: orderData.loadingStation?.code,
          loadingRoad: orderData.loadingRoad?.code,
          totalWagons: orderData.totalWagons ?? 0,
          unloadingDirection: orderData.unloadingDirection?.map((i) => ({
            unloadingRoad: i.unloadingRoad?.code ?? "",
            unloadingStation: i.unloadingStation?.code ?? "",
            countWagons: i.countWagons ?? 0,
            tariffStation: i.tariffRoad,
            roadCode: i.unloadingRoad?.code ?? "",
            stationCode: i.unloadingStation?.code ?? "",
          })),
          // Торги
          tradeBegin: formatDatetimeLocal(orderData.tradeBegin) ?? "",
          tradeEnd: formatDatetimeLocal(orderData.tradeEnd ?? orderData.resultsDate) ?? "",
          collectionBegin: formatDatetimeLocal(orderData.collectionBegin) ?? "",
          collectionEnd: formatDatetimeLocal(orderData.collectionEnd) ?? "",
          retrading: orderData.retrading,
          retradingBegin: formatDatetimeLocal(orderData.retradingBegin) ?? "",
          retradingEnd: formatDatetimeLocal(orderData.retradingEnd) ?? "",
          resultsDate: formatDatetimeLocal(orderData.tradeEnd ?? orderData.resultsDate) ?? "",
          // График подачи вагонов
          deadlineAt: orderData.deadlineAt ?? "",
          deadlineTo: orderData.deadlineTo ?? "",
          decade: orderData.decade,
          decadeFirst: orderData.decadeFirst,
          decadeSecond: orderData.decadeSecond,
          decadeThird: orderData.decadeThird,
          decadeComments: orderData.decadeComments,
        });
    }, [orderData]);

    const handleFormSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      form.trigger().then((isValid) => {
        if (isValid) {
          setShowSubmitConfirm(true);
        }
      });
    };

    return (
      <FormProvider {...form}>
        <form className="space-y-6 md:space-y-8" onSubmit={handleFormSubmit} noValidate>
          <CustomerCard />
          <InformationCard />
          <DirectionTransportationCard />
          <CargoParamsCard />
          <ScheduleNormsCard />
          <WagonDeliveryCard />
          <TradesCard />

          {hasErrors && (
            <div className="text-sm text-destructive">
              При заполнении формы заявки произошли ошибки - их необходимо исправить
            </div>
          )}

          <div className="flex flex-col md:justify-end md:flex-row gap-3 py-2 md:py-0 ">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.reset();
                form.clearErrors();
                setLocation(ROUTES.ROOT);
              }}
            >
              Отмена
            </Button>

            {orderData?.status === OrderDetailResponseDTOStatus.DRAFT && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowDraftConfirm(true);
                }}
                disabled={isPending}
              >
                Сохранить черновик
              </Button>
            )}

            <Button type="submit" disabled={isPending} className="btn-primary">
              Отправить на проверку
            </Button>
          </div>
        </form>

        <ConfirmModal
          isOpen={showDraftConfirm}
          title="Сохранение черновика"
          message="Сохранить заявку как черновик?"
          onConfirm={() => {
            form.clearErrors();
            onDraft(form.getValues());
            setShowDraftConfirm(false);
          }}
          onCancel={() => setShowDraftConfirm(false)}
          confirmText="Сохранить"
          cancelText="Отмена"
        />

        <ConfirmModal
          isOpen={showSubmitConfirm}
          title="Отправка на проверку"
          message="Отправить заявку на проверку администратору?"
          onConfirm={() => {
            form.clearErrors();
            onSubmit(form.getValues());
            setShowSubmitConfirm(false);
          }}
          onCancel={() => setShowSubmitConfirm(false)}
          confirmText="Отправить"
          cancelText="Отмена"
        />
      </FormProvider>
    );
  },
);
